import React, { useState, forwardRef } from 'react'
import classNames from 'classnames'
import { FieldElement } from 'react-hook-form'
import './style.css'

const INPUT_HEIGHT = 40

type SideRenderProps = ({
  className,
}: {
  className?: string
}) => React.ReactElement | null

export type InputProps = React.InputHTMLAttributes<FieldElement> & {
  margin?: 'top' | 'bottom' | 'both'
  loading?: boolean
  valid?: boolean
  error?: boolean
  highlighted?: boolean
  renderRight?: SideRenderProps
  renderLeft?: SideRenderProps
  'data-test-id'?: string
}

// eslint-disable-next-line react/display-name
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      disabled,
      className,
      style,
      margin,
      error,
      renderRight,
      renderLeft,
      loading,
      highlighted,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false)

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      if (props.onFocus) {
        props.onFocus(e)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      if (props.onBlur) {
        props.onBlur(e)
      }
    }

    const RightPart = renderRight
      ? renderRight({
          className: 'shade-30 margin-horizontal-12',
        })
      : null

    const LeftPart = renderLeft
      ? renderLeft({
          className: 'shade-30 margin-horizontal-12',
        })
      : null

    const _className = classNames(
      {
        'margin-bottom-8': margin === 'both' || margin === 'bottom',
        'margin-top-8': margin === 'both' || margin === 'top',
      },
      'position-relative',
      'font-family-normal font-size-normal',
      'transition-short',
      'flexbox flexbox-row flexbox-align-center flexbox-justify-center',
      'radius-large font-weight-bold font-size-normal',
      'background-shade-100',
      'border-small border-solid border-shade-30-on-hover',
      {
        'border-red': error,
        'border-shade-50': disabled,
        'border-shade-45': !error && !disabled && !isFocused,
        'border-shade-40': !error && !disabled && isFocused,
        highlighted: !error && highlighted,
      },
      className
    )
    return (
      <label className={_className}>
        {LeftPart}
        <input
          style={{ height: INPUT_HEIGHT, ...style }}
          disabled={disabled}
          className={classNames(
            'background-shade-100',
            'font-size-normal flexbox-grow border-none height-full radius-large',
            'placeholder-shade-20',
            renderRight ? 'padding-right-12' : 'padding-right-24',
            renderLeft ? 'padding-left-12' : 'padding-left-24',
            disabled ? 'shade-30' : 'shade-05'
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={ref}
          {...props}
        />
        {RightPart}
      </label>
    )
  }
)
